import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Reads ?focus=<id> from the URL and, once `items` has loaded, scrolls the element
 * carrying `data-focus-id="<id>"` into view and briefly highlights it.
 *
 *   const focusedId = useFocusItem(projects);
 *   <div data-focus-id={p.id} className={focusedId === p.id ? 'ring-2 ring-amber-400' : ''}>
 *
 * The hook only triggers once per (id, items-loaded) pair.
 */
export default function useFocusItem(items) {
  const location = useLocation();
  const triggered = useRef(null);

  const params = new URLSearchParams(location.search);
  const focusId = params.get('focus');

  useEffect(() => {
    if (!focusId || !items || items.length === 0) return;
    // de-dup so we don't re-scroll on every items change
    const key = `${location.pathname}::${focusId}::${items.length}`;
    if (triggered.current === key) return;
    triggered.current = key;
    // wait one frame so the DOM is fully rendered
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-focus-id="${focusId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('focus-ring-pulse');
        setTimeout(() => el.classList.remove('focus-ring-pulse'), 2800);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [focusId, items, location.pathname]);

  return focusId;
}
